import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import i18n from '../../i18n';

/**
 * Formats the booking dates for arrival and departure display.
 * @param {string} dateString - The date string.
 * @param {string} formatString - The format to use.
 * @param {string} locale - The locale to use.
 * @returns {string} The formatted date.
 */
export const formatBookingDateLong = (
  dateString,
  formatString = 'eeee dd MMMM',
  locale = i18n.language,
) => {
  const date = new Date(`${dateString} 00:00:00`);

  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const formatted = format(date, formatString, { locale: locale === 'fr' ? fr : enUS });
  return `${formatted.charAt(0).toUpperCase() + formatted.slice(1)}`;
};

/**
 * Get the total number of passengers form booking object.
 * @param {Passengers} passengers - The passengers.
 * @param {string[]} excludedKeys - The keys to exclude from the total count.
 * @returns {number} The total number of passengers.
 */
export const getPassengersTotalCount = (
  passengers,
  excludedKeys = ['animals'],
) => {
  if (!passengers || typeof passengers !== 'object') {
    return 0;
  }
  return Object.keys(passengers)
    .filter((
      key,
    ) => !excludedKeys.includes(key))
    .map((key) => /** @type {any} */ (passengers)[key])
    .reduce(
      /**
       * @param {number} acc - The accumulator.
       * @param {number} total passenger type total.
       * @returns {number} The total number of passengers.
       */
      (acc, total) => acc + total,
      0,
    );
};

/**
 * Get the total number of luggage form booking object.
 * @param {Luggage} luggage - The luggage of all types.
 * @returns {number} The total number of luggage.
 */
export const getLuggageTotalCount = (luggage) => {
  if (!luggage || typeof luggage !== 'object') {
    return 0;
  }
  return Object.values(luggage).reduce(
  /**
   * @param {number} acc - The accumulator.
   * @param {number} total - luggage size total.
   * @returns {number} The total number of luggage.
   */
    (acc, total) => acc + total,
    0,
  );
};

/**
 * Capitalize the first letter of a string.
 * @param {string} value - The value to capitalize.
 * @returns {string} The capitalized value.
 */
export const capitalizedValue = (value) => {
  if (value) {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }
  return '';
};

/**
 * Format time string based on locale
 * @param {string} timeString - Time in format "hh:mm"
 * @param {string} locale - The locale to use
 * @returns {string} - Formatted time string
 */
export const formatTimeString = (timeString, locale = i18n.language) => {
  if (!timeString) return '';

  const [hours, minutes] = timeString.split(':');
  const date = new Date();
  date.setHours(parseInt(hours, 10));
  date.setMinutes(parseInt(minutes, 10));

  const options = /** @type {const} */({
    hour: '2-digit',
    minute: '2-digit',
    hour12: locale !== 'fr',
  });

  return date.toLocaleTimeString(locale, options);
};

/**
 * Format terminal opening hours
 * @param {string} opening - The opening time
 * @param {string} closing - The closing time
 * @param {string} locale - The locale to use
 * @returns {string} - The formatted opening hours
 */
export const formatTerminalOpeningHours = (opening, closing, locale = i18n.language) => {
  const formattedOpening = formatTimeString(opening, locale) || '-';
  const formattedClosing = formatTimeString(closing, locale) || '-';
  return `${formattedOpening} ${i18n.t('common.to')} ${formattedClosing}`;
};

/** @type {('adult' | 'child' | 'infant' | 'animal')[]} */
export const PASSENGER_DISPLAY_CATEGORIES = ['adult', 'child', 'infant', 'animal'];

/**
 * Group passengers by display category for the flight brief PDF.
 * @param {User[]} [passengersList] - List of passengers.
 * @returns {Record<string, User[]>}
 */
export const groupPassengersByCategory = (passengersList) => (
  passengersList ?? []
).reduce((acc, passenger) => {
  if (!acc[passenger.category]) acc[passenger.category] = [];
  acc[passenger.category].push(passenger);
  return acc;
}, /** @type {Record<string, User[]>} */ ({
  adult: [],
  child: [],
  infant: [],
  animal: [],
}));

/**
 * Build a comma-separated list of passenger full names.
 * @param {User[]} [passengers] - Passengers of a single category.
 * @returns {string} The joined names.
 */
export const joinPassengerNames = (passengers) => (passengers ?? [])
  .map((passenger) => passenger.fullname || '')
  .filter(Boolean)
  .join(', ');

/**
 * Check whether any passenger category has at least one entry.
 * @param {Record<string, User[]>} [passengersByCategory] - Passengers grouped by category.
 * @param {string[]} [categories] - Categories to check.
 * @returns {boolean}
 */
export const hasPassengersByCategory = (
  passengersByCategory,
  categories = PASSENGER_DISPLAY_CATEGORIES,
) => categories.some((category) => passengersByCategory?.[category]?.length);

/**
 * Extract the uppercase IATA airport code.
 * @param {object} [airport] - Airport object.
 * @param {string} [airport.airportCode] - Airport IATA code.
 * @returns {string}
 */
export const getAirportCode = (airport) => airport?.airportCode?.toUpperCase() ?? '';

/**
 * Format the PDF footer generation timestamp.
 * @param {string} locale - The locale to use.
 * @param {Date} [date] - The date to format.
 * @returns {string}
 */
export const formatPdfGeneratedAt = (locale, date = new Date()) => format(
  date,
  'dd/MM/yyyy HH:mm:ss',
  { locale: locale === 'fr' ? fr : enUS },
);

/**
 * Maps each {@link Passengers} quota field to the customer passenger category.
 * Keep in sync with {@link Passengers}.
 * @type {Record<keyof Passengers, 'adult' | 'child' | 'infant' | 'animal'>}
 */
export const PASSENGER_CATEGORY_BY_BOOKING_KEY = {
  adults: 'adult',
  children: 'child',
  infants: 'infant',
  animals: 'animal',
};

/**
 * Count passengers for a given category.
 * @param {string} category
 * @param {Array<{ category?: string }>} [passengers]
 * @returns {number}
 */
export const countPassengersByCategory = (category, passengers = []) => (
  passengers
).filter((passenger) => passenger.category === category).length;

/**
 * Return passenger categories whose quota would be exceeded by the current selection.
 * @param {object} params
 * @param {Passengers} [params.allowedPassengersByCategory]
 * @param {Array<{ category?: string }>} [params.briefPassengers]
 * @param {number[]} [params.selectedPassengerIds]
 * @param {Array<{ id?: number, category?: string, fullname?: string }>} [params.availablePassengers]
 * @returns {string[]}
 */
export const getExceededPassengerCategories = ({
  allowedPassengersByCategory,
  briefPassengers = [],
  selectedPassengerIds = [],
  availablePassengers = [],
}) => {
  if (allowedPassengersByCategory == null) {
    return [];
  }

  const selectedPassengers = availablePassengers.filter(
    (passenger) => selectedPassengerIds.includes(passenger.id),
  );

  return (/** @type {(keyof Passengers)[]} */ (
    Object.keys(PASSENGER_CATEGORY_BY_BOOKING_KEY)
  ))
    .filter((bookingKey) => {
      const category = PASSENGER_CATEGORY_BY_BOOKING_KEY[bookingKey];
      const allowed = allowedPassengersByCategory[bookingKey] ?? 0;
      const currentCount = countPassengersByCategory(category, briefPassengers);
      const selectedCount = countPassengersByCategory(category, selectedPassengers);

      return selectedCount > 0 && currentCount + selectedCount > allowed;
    })
    .map((bookingKey) => PASSENGER_CATEGORY_BY_BOOKING_KEY[bookingKey]);
};
